import { JSX, ReactNode, useEffect, useState } from 'react';
import { AnalyticsUserContext } from '@src/contexts/analytics-user-context';
import { AnalyticsUserContextResponse } from '@src/@types/analytics-user';
import { useAnalyticsUserContextApi } from '@src/hooks/use-analytics-user-context-api.hook';

/**
 * Issues a fetch to the SFPA Analytics User Context endpoint and stores the
 * useful portion of it in {@link AnalyticsUserContext}.
 */
export const AnalyticsUserProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<Error>(null);
  const [analyticsUserContextResponse, setAnalyticsUserContextResponse] =
    useState<AnalyticsUserContextResponse>(null);
  const { fetchAnalyticsUserContext } = useAnalyticsUserContextApi();
  useEffect(() => {
    let mounted = true;

    if (!loaded) {
      (async () => {
        try {
          const analyticsUserContextData = await fetchAnalyticsUserContext();
          if (mounted) {
            setAnalyticsUserContextResponse(analyticsUserContextData);
            setLoaded(true);
            setError(null);
          }
        } catch (reason) {
          if (mounted) {
            console.error('Failed to fetch user context.', reason);
            setError(reason);
            setLoaded(false);
            setAnalyticsUserContextResponse(null);
          }
        }
      })();
    }

    return () => {
      mounted = false;
    };
  }, [loaded]);

  return (
    <AnalyticsUserContext.Provider
      value={{
        loaded,
        error,
        analyticsUserContextResponse: analyticsUserContextResponse,
      }}
    >
      {children}
    </AnalyticsUserContext.Provider>
  );
};
